import { useAuth } from "@/hooks/useAuth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

export type SubscriptionTier = 'scout' | 'adventurer' | 'organizer';
export type BillingInterval = 'monthly' | 'annual' | null;

interface SubscriptionState {
  tier: SubscriptionTier;
  isLoading: boolean;
  canAccess: (requiredTier: 'adventurer' | 'organizer') => boolean;
  billingInterval: BillingInterval;
  currentPeriodEnd: string | null;
  isBetaUser: boolean;
  isSubscribed: boolean;
  refreshSubscription: () => void;
}

const TIER_HIERARCHY: Record<SubscriptionTier, number> = {
  scout: 0,
  adventurer: 1,
  organizer: 2,
};

export const useSubscription = (): SubscriptionState => {
  const { user, session, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Guard to prevent multiple session-expired toasts/redirects
  const hasHandledAuthError = useRef(false);
  // Counter for consecutive auth failures — only sign out after 3
  const authErrorCount = useRef(0);

  const { data: subscription, isLoading } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      // Query the users table for subscription info
      const { data, error } = await supabase
        .from('users')
        .select('subscription_tier, subscription_status, subscription_billing_interval, subscription_current_period_end, beta_access')
        .eq('id', user.id)
        .single();

      if (error || !data) {
        console.error('Error fetching subscription:', error);
        return null;
      }

      return data;
    },
    enabled: !!user?.id && !authLoading,
  });

  // Reset auth error flags when user changes
  useEffect(() => {
    hasHandledAuthError.current = false;
    authErrorCount.current = 0;
  }, [user?.id]);

  // Refresh subscription from Stripe periodically
  useEffect(() => {
    // Don't run if auth is still loading or no authenticated session
    if (authLoading || !user?.id || !session?.access_token) {
      return;
    }

    const checkSubscription = async () => {
      try {
        // Use the session from context - it's already validated
        const accessToken = session.access_token;
        
        if (!accessToken) {
          console.log('No access token available, skipping subscription check');
          return;
        }

        const { data, error } = await supabase.functions.invoke('check-subscription', {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        
        if (error) {
          // Only treat HTTP 401/403 status codes as auth errors
          const isAuthError = 
            (error.context?.status === 401) ||
            (error.context?.status === 403);
          
          if (isAuthError) {
            authErrorCount.current += 1;
            console.warn(`Auth error from check-subscription (${authErrorCount.current}/3)`, error.message);
            
            // Only sign out after 3 consecutive auth failures
            if (authErrorCount.current >= 3 && !hasHandledAuthError.current) {
              hasHandledAuthError.current = true;
              console.warn('3 consecutive auth failures, signing out locally');
              
              await supabase.auth.signOut({ scope: 'local' });
              queryClient.invalidateQueries({ queryKey: ['subscription'] });
              
              toast({
                title: "Session expired",
                description: "Please sign in again to continue.",
                variant: "default",
              });
              
              navigate('/auth');
            }
            return;
          }
          
          // For non-auth errors, just log
          console.warn('Subscription check failed (non-auth):', error.message);
          return;
        }
        
        // Success — reset the consecutive error counter
        authErrorCount.current = 0;
        
        // Refetch local data after syncing with Stripe
        queryClient.invalidateQueries({ queryKey: ['subscription', user.id] });
      } catch (error) {
        // Catch-all for unexpected errors - don't surface to user
        console.warn('Error checking subscription:', error);
      }
    };

    // Small delay to ensure everything is fully initialized
    const timeoutId = setTimeout(checkSubscription, 1000);

    // Check every 5 minutes
    const interval = setInterval(checkSubscription, 5 * 60 * 1000);
    return () => {
      clearTimeout(timeoutId);
      clearInterval(interval);
    };
  }, [user?.id, session?.access_token, authLoading, queryClient, navigate, toast]);

  const refreshSubscription = () => {
    queryClient.invalidateQueries({ queryKey: ['subscription', user?.id] });
  };

  // Direct mapping since enums now match
  const currentTier = (subscription?.subscription_tier as SubscriptionTier) || 'scout';
  const billingInterval = (subscription?.subscription_billing_interval as BillingInterval) || null;
  const currentPeriodEnd = subscription?.subscription_current_period_end || null;
  const isBetaUser = subscription?.beta_access || false;
  const isSubscribed = currentTier !== 'scout';

  const canAccess = (requiredTier: 'adventurer' | 'organizer'): boolean => {
    if (!user) return false;
    
    // Beta users get full access to all premium features
    if (isBetaUser) return true;
    
    if (subscription?.subscription_status && subscription.subscription_status !== 'active') {
      return false;
    }

    return TIER_HIERARCHY[currentTier] >= TIER_HIERARCHY[requiredTier];
  };

  return {
    tier: currentTier,
    isLoading,
    canAccess,
    billingInterval,
    currentPeriodEnd,
    isBetaUser,
    isSubscribed,
    refreshSubscription,
  };
};
